import { PropsWithChildren, ReactElement, ReactNode } from "react"
import { Breakpoint, Dialog, DialogProps, PaperProps, useMediaQuery, useTheme } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { makeStyles } from "tss-react/mui"

import { ErrorBoundaryModal } from "../errors/ErrorBoundaryModal"
import { HIDDEN_IN_PRINT } from "../print/printPageOnly"
import { SafeArea } from "../skeleton/SafeArea"
import { StandardDialogTopbar, StandardTopbar } from "../skeleton/TopBar"

type StyleProps = Readonly<{
    fullScreen: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { fullScreen }) => ({
    // For wide screens we add blur to avoid distractions from dialog content
    dialogBackdrop: {
        [theme.breakpoints.up("sm")]: {
            backdropFilter: "blur(10px)",
        },
    },
    // This ensures that contained elements with position: fixed will be positioned relative to the paper instead of viewport
    // Specifically, this prevents top bar and floating buttons from ending up outside the dialog when it is not full screen
    // See: https://developer.mozilla.org/en-US/docs/Web/CSS/position
    dialogPaper: {
        minHeight: "18rem",
        transform: fullScreen ? undefined : "scaleX(1)",
    },
    scrollShadowBottom: {
        "&::after": {
            zIndex: 1,
            position: fullScreen ? "fixed" : "sticky",
            content: '"-"',
            bottom: "-25px",
            left: "0",
            width: "100%",
            boxShadow: "0 -5px 45px rgb(0 0 0 / 80%)",
        },
    },
    edge: {
        height: "10px",
        ...HIDDEN_IN_PRINT,
    },
}))

export type StandardDialogProps = Readonly<
    PropsWithChildren<{
        titleElement?: ReactNode
        topbar?: ReactElement
        open: boolean
        onClose: () => void
        disableBackdropClick?: boolean
        maxWidthProp?: Breakpoint
        scroll?: DialogProps["scroll"]
        hideCloseIcon?: boolean
        extraActions?: ReactNode
        handleBackButton?: () => void
        backButtonText?: string
        dialogTitle?: ReactNode
        titleLeftIcon?: ReactNode
        subtitle?: ReactNode
        slotProps?: {
            paper?: PaperProps
            root?: { sx?: { zIndex?: number } }
        }
        updateEdge?: string
        isFullScreen?: boolean
        // Keep the windowed look (with close button) on narrow screens instead of going full screen
        disableMobileFullScreen?: boolean
        disableScrollLock?: boolean
        updateDialogClasses?: {
            dialogTitleContainer?: string
        }
    }>
>

export function StandardDialog({
    titleElement,
    topbar,
    open,
    children,
    onClose,
    disableBackdropClick = false,
    maxWidthProp = "sm",
    scroll = "paper",
    hideCloseIcon = false,
    extraActions = false,
    handleBackButton,
    backButtonText,
    dialogTitle,
    titleLeftIcon,
    subtitle,
    slotProps = {},
    updateEdge = "",
    updateDialogClasses,
    isFullScreen = false,
    disableMobileFullScreen = false,
    disableScrollLock = false,
}: StandardDialogProps) {
    const { ref: bottomRef, inView: isAtBottom } = useInView({ threshold: 1.0 })
    const theme = useTheme()
    const wide = useMediaQuery(theme.breakpoints.up("sm"))

    // On a wide display we never use full screen; `disableMobileFullScreen` keeps that on narrow screens too.
    const fullScreen = isFullScreen || (!wide && !disableMobileFullScreen)

    const { classes, cx } = useStyles({ fullScreen })

    const { paper: paperProps = {}, root: rootProps } = slotProps

    const renderTopBar = () => {
        // TODO: need to remove these conditions (topbar, titleElement) when we change the use of StandardDialog in the files
        if (topbar) {
            return topbar
        } else if (titleElement) {
            return (
                <StandardTopbar
                    middleElement={titleElement}
                    backButtonType="close"
                    hideMenuButton
                    onBackButton={onClose}
                    onMenuButton={undefined}
                />
            )
        }

        return (
            <StandardDialogTopbar
                dialogTitleClasses={updateDialogClasses?.dialogTitleContainer}
                dialogTitle={dialogTitle}
                titleLeftIcon={titleLeftIcon}
                subtitle={subtitle}
                hideCloseIcon={hideCloseIcon}
                handleBackButton={handleBackButton}
                backButtonText={backButtonText}
                extraActions={extraActions}
                onClose={onClose}
            />
        )
    }

    return (
        <Dialog
            disableScrollLock={disableScrollLock}
            open={open}
            onClose={(event, reason) => {
                if (disableBackdropClick && reason === "backdropClick") {
                    return
                }

                onClose()
            }}
            maxWidth={fullScreen ? undefined : maxWidthProp}
            fullWidth={fullScreen ? undefined : true}
            fullScreen={fullScreen}
            scroll={scroll}
            slotProps={{
                backdrop: { classes: { root: classes.dialogBackdrop } },
                root: rootProps,
            }}
            PaperProps={paperProps}
            classes={{
                paper: cx(classes.dialogPaper, paperProps.className),
                paperScrollPaper: cx({
                    // TODO: We disable scroll shadow due to weird bug in Chrome
                    // with position:sticky causing the scroll to jump around
                    // Re-enable when we have time to debug the issue
                    [classes.scrollShadowBottom]: false, // !isAtBottom
                }),
            }}
        >
            <ErrorBoundaryModal onClose={onClose}>
                <SafeArea>
                    {renderTopBar()}
                    {children}
                    <div ref={bottomRef} className={cx(classes.edge, updateEdge)}></div>
                </SafeArea>
            </ErrorBoundaryModal>
        </Dialog>
    )
}
